import { store } from "../store";
import DataStructure, { sanitize } from "./DataStructure";
import { ADVANCE_TYPE_PAID_IN_FULL } from "./Commission";
import { COLORS } from "../constants";
import {
  deepFreeze,
  formatCurrency,
  roundPrice,
  timestampToDate,
} from "../util/misc";
import { compact, cloneDeep, every, get, isEmpty, uniq } from "lodash";
import { diff } from "deep-object-diff";

export const PAYCHECK_FIELDS = {
  amount: 0,
  amountCommission: 0,
  amountManagerShare: 0, // Manager share earnings from location-based sales
  amountDraw: 0,
  amountFromHeld: 0,
  amountToHeld: 0,
  commission: [],
  // Advanced Commission Payments claimed on this paycheck, as
  // { [commissionID]: { amount, type, note } }. The amount is snapshotted
  // here at approval: for the paid-in-full claim it is 50% of EXPECTED
  // commission, which depends on the salesperson's own bracket schedule —
  // client-side knowledge the server cannot recompute — and for a manual
  // advance it is whatever payroll entered. touchPaycheck reads it back to
  // write the advance onto the commission document, keyed by `type`.
  //
  // Checks saved before manual advances existed stored a bare number, which
  // always meant the paid-in-full claim. Read entries through
  // `Paycheck.advanceEntry`, never directly.
  advances: {},
  managerShares: [], // Array of manager share IDs included in this paycheck

  // Override
  override: null,
  overrideReason: "",
  overrideConfirmed: false,

  // Status
  payment: null, // Deprecated - kept for backwards compatibility
  payrollDate: null, // Date the payroll was processed
  payrollNumber: "", // Payroll reference number
  locked: false,

  // Meta info
  created: null,
  createdBy: null,
  modified: null,
  modifiedBy: null,
};
deepFreeze(PAYCHECK_FIELDS);

export const PAYCHECK_STATUS_TYPES = {
  pending: {
    order: 1,
    label: "Pending approval",
    icon: "undone",
    color: "warning",
  },
  override: {
    order: 2,
    label: "Pending override confirmation",
    icon: "undone",
    color: "warning",
  },
  overrideConfirmed: {
    order: 3,
    label: "Override confirmed",
    icon: "done",
    color: "warning",
  },
  paid: {
    order: 4,
    label: "Paid",
    icon: "done",
    color: "positive",
  },
};
deepFreeze(PAYCHECK_STATUS_TYPES);

export default class Paycheck extends DataStructure {
  constructor(userID, id, data = {}) {
    data = sanitize(data, PAYCHECK_FIELDS);

    // Convert to Date
    data.created = timestampToDate(data.created);
    data.modified = timestampToDate(data.modified);
    data.payrollDate = timestampToDate(data.payrollDate);

    super(data);

    this.id = id || null;
    this.userID = userID;
    this.permissions = Object.freeze({
      view: Paycheck.canBeViewedBy,
      edit: this.canBeEditedBy,
      delete: this.canBeDeletedBy,
      unlock: this.canBeUnlockedBy,
      override: this.canBeOverriddenBy,
    });
  }

  // Permissions

  static canBeViewedBy(user) {
    return user.isAdmin || (user.isEmployee && user.hasRole("payroll"));
  }

  canBeEditedBy(user) {
    return (
      (user.isAdmin ||
        user.id === this.userID ||
        (user.isEmployee && user.hasRole("payroll"))) &&
      (!this.id || !this.locked || this.canBeUnlockedBy(user))
    );
  }

  canBeDeletedBy(user) {
    // Only unpaid paychecks can be voided. Once payrollDate or payment is
    // set the paycheck has affected balances and notified the employee, so
    // it must be reversed through the proper accounting flow.
    if (this.data.payrollDate || this.data.payment) {
      return false;
    }
    if (this.data.locked && !this.canBeUnlockedBy(user)) {
      return false;
    }
    return (
      user.isAdmin ||
      user.id === this.userID ||
      (user.isEmployee && user.hasRole("payroll"))
    );
  }

  canBeUnlockedBy(user) {
    return user.isAdmin;
  }

  canBeOverriddenBy(user) {
    return user.isAdmin || (user.isEmployee && user.hasRole("payroll"));
  }

  // Database

  get path() {
    return Paycheck.getPath(this.userID, this.id);
  }

  static getPath(userID, id) {
    let path = `users/${userID}/paychecks`;
    if (id) {
      path += `/${id}`;
    }
    return path;
  }

  static toFirestore(paycheck) {
    return paycheck.data;
  }

  static fromFirestore(snapshot, options) {
    const data = snapshot.data(options);
    return new Paycheck(snapshot.ref.parent.parent.id, snapshot.id, data);
  }

  // Advances

  /**
   * Normalise one `advances` snapshot entry to `{ amount, type, note }`.
   *
   * A bare number is a check saved before manual advances existed, when the
   * only claim possible was the paid-in-full one. Returns null for a missing
   * entry so callers can use `in`-style presence checks through it.
   */
  static advanceEntry(value) {
    if (value === undefined || value === null) {
      return null;
    }
    if (typeof value === "object") {
      return {
        amount: roundPrice(value.amount || 0),
        type: value.type || ADVANCE_TYPE_PAID_IN_FULL,
        note: value.note || "",
      };
    }
    return {
      amount: roundPrice(Number(value) || 0),
      type: ADVANCE_TYPE_PAID_IN_FULL,
      note: "",
    };
  }

  static advanceAmount(value) {
    const entry = Paycheck.advanceEntry(value);
    return entry ? entry.amount : 0;
  }

  // The advance this check claims for a commission, or null when the entry
  // on it is a whole-commission or pay-app payment.
  getAdvance(commissionID) {
    return Paycheck.advanceEntry((this.data.advances || {})[commissionID]);
  }

  hasAdvance(commissionID) {
    return this.getAdvance(commissionID) !== null;
  }

  // Editing

  get rules() {
    return {
      amountToHeld: [
        (a) =>
          !a ||
          (!this.data.amountFromHeld && !this.data.amountDraw) ||
          "holdDrawConflict",
        // `Math.max(0, …)` because a clawback makes commissionTotal NEGATIVE,
        // and a negative ceiling failed this rule at every a >= 0 — including
        // a === 0, which made the whole paycheck unsaveable. There is simply
        // nothing to withhold on such a check.
        (a) =>
          (a >= 0 &&
            a <=
              Math.max(
                0,
                this.commissionTotal || this.data.amountCommission || 0
              )) ||
          "holdExceedsCommission",
      ],
      amountFromHeld: [
        (a) =>
          (a >= 0 && a <= Math.max(0, this.previouslyHeld)) ||
          "fromHeldExceeds",
      ],
      amountDraw: [
        (a) =>
          (a >= 0 && a <= (this.user?.data?.drawAmount || 0)) ||
          "drawExceedsAllowed",
      ],
      overrideReason: [
        (a) =>
          !this.id || !this.hasOverride || Boolean(a && a.trim()) || "required",
      ],
      "override.amountCommission": [
        (a) =>
          !this.id ||
          !this.data.override ||
          a >= 0 ||
          "overrideCommissionNegative",
      ],
      "override.amountToHeld": [
        (a) =>
          !this.id ||
          !this.data.override ||
          !a ||
          (!this.data.override.amountFromHeld &&
            !this.data.override.amountDraw) ||
          "holdDrawConflict",
        (a) =>
          !this.id ||
          !this.data.override ||
          (a >= 0 && a <= this.data.override.amountCommission) ||
          "overrideHoldExceedsCommission",
      ],
      payrollDate: [
        (v) =>
          !this.id || !this.data.overrideConfirmed || Boolean(v) || "required",
      ],
      payrollNumber: [
        (v) =>
          !this.id || !this.data.overrideConfirmed || Boolean(v) || "required",
      ],
    };
  }

  get isValid() {
    return every(this.rules, (rules, key) =>
      rules.every((rule) => rule(get(this.data, key)) === true)
    );
  }

  get isEmpty() {
    return (
      !this.id &&
      isEmpty(
        diff(
          this.sanitize(cloneDeep(this.data)),
          this.sanitize({
            ...PAYCHECK_FIELDS,
            amountDraw: this.user ? this.user.data.drawAmount : 0,
          })
        )
      )
    );
  }

  isEqual(paycheck) {
    return (
      this.id === paycheck.id &&
      isEmpty(
        diff(
          this.sanitize(cloneDeep(this.data)),
          this.sanitize(cloneDeep(paycheck.data))
        )
      )
    );
  }

  sanitize(data = this.data) {
    data.amountCommission = roundPrice(data.amountCommission || 0);
    data.amountDraw = roundPrice(data.amountDraw || 0);
    data.amountFromHeld = roundPrice(data.amountFromHeld || 0);
    data.amountToHeld = roundPrice(data.amountToHeld || 0);
    data.amount = this.total;
    if (!data.override) {
      data.override = null;
    } else {
      Object.keys(data.override).forEach((key) => {
        if (data[key] === data.override[key]) {
          delete data.override[key];
        }
      });
      if (isEmpty(data.override)) {
        data.override = null;
      }
    }
    if (!data.override) {
      data.overrideReason = "";
      data.overrideConfirmed = false;
    } else {
      if ("amountCommission" in data.override) {
        data.override.amountCommission = roundPrice(
          data.override.amountCommission
        );
      }
      if ("amountDraw" in data.override) {
        data.override.amountDraw = roundPrice(data.override.amountDraw);
      }
      if ("amountFromHeld" in data.override) {
        data.override.amountFromHeld = roundPrice(data.override.amountFromHeld);
      }
      if ("amountToHeld" in data.override) {
        data.override.amountToHeld = roundPrice(data.override.amountToHeld);
      }
      data.override.amount = roundPrice(
        (("amountCommission" in data.override
          ? data.override.amountCommission
          : data.amountCommission) || 0) +
          (("amountFromHeld" in data.override
            ? data.override.amountFromHeld
            : data.amountFromHeld) || 0) +
          (("amountDraw" in data.override
            ? data.override.amountDraw
            : data.amountDraw) || 0) -
          (("amountToHeld" in data.override
            ? data.override.amountToHeld
            : data.amountToHeld) || 0)
      );
    }
    return data;
  }

  get hasOverride() {
    return (
      Boolean(this.data.override) &&
      !isEmpty(this.data.override) &&
      Object.keys(this.data.override).some(
        (key) => this.data.override[key] !== this.data[key]
      )
    );
  }

  // Formatting

  get amount() {
    const dollars = this.data.amount;
    return dollars !== null ? formatCurrency(dollars) : null;
  }

  get amountCommission() {
    const dollars = this.data.amountCommission;
    return dollars !== null ? formatCurrency(dollars) : null;
  }

  get amountDraw() {
    const dollars = this.data.amountDraw;
    return dollars !== null ? formatCurrency(dollars) : null;
  }

  get amountFromHeld() {
    const dollars = this.data.amountFromHeld;
    return dollars !== null ? formatCurrency(dollars) : null;
  }

  get amountToHeld() {
    const dollars = this.data.amountToHeld;
    return dollars !== null ? formatCurrency(dollars) : null;
  }

  get saleIDs() {
    return uniq(this.data.commission.map((id) => id.split("-")[0])).sort();
  }

  get sales() {
    return compact(this.saleIDs.map((id) => store.state.main.sales[id]));
  }

  get payment() {
    return this.data.payment
      ? store.state.main.transactions[this.data.payment]
      : null;
  }

  get paidDate() {
    // Return payrollDate if set, otherwise fall back to payment transaction date for legacy
    if (this.data.payrollDate) {
      return this.data.payrollDate;
    }
    return this.payment?.data?.date || null;
  }

  get commission() {
    return compact(
      this.saleIDs.map((id) => store.state.main.commission[id + this.userID])
    );
  }

  get commissionTotal() {
    return roundPrice(
      this.data.commission.reduce((total, id) => {
        const [cID, payAppID] = id.split("-");
        const c = store.state.main.commission[cID + this.userID];
        if (!c) {
          return total;
        }
        // `|| 0` on each: getPayAppCommission returns null for a pay app
        // that has since been voided off the commission, and
        // remainingCommission returns null while actual figures are unknown.
        // `total + null` coerces to `total` anyway, so this changes no
        // arithmetic -- it makes the intent explicit and matches
        // getSaleCommissionAmount.
        if (payAppID) {
          return total + (c.getPayAppCommission(payAppID) || 0);
        }
        // An advance claimed on THIS paycheck pays the snapshotted amount:
        // the sale is not finished, so there is no actual commission yet.
        const advance = this.getAdvance(cID);
        if (advance) {
          return total + advance.amount;
        }
        // Otherwise a whole-commission entry pays the TRUE-UP, not the
        // gross: anything already advanced has to come off or it gets paid
        // twice. `remainingCommission` also accounts for splits.
        return total + (c.remainingCommission || 0);
      }, 0)
    );
  }

  /**
   * Commission amount contributed by a single sale on this paycheck. Sums
   * across all commission entries (full-commission or per-payApp) whose
   * saleID portion matches. Returns null if no matching entries are found,
   * or none of their commission records is loaded -- an unloaded record is
   * an unknown amount, not $0.00 (every line of a paycheck opened from the
   * Paychecks page read $0.00 that way).
   */
  getSaleCommissionAmount(saleID) {
    const entries = this.data.commission.filter(
      (id) => id.split("-")[0] === saleID
    );
    if (!entries.length) {
      return null;
    }
    let loaded = false;
    const total = entries.reduce((sum, id) => {
      const [cID, payAppID] = id.split("-");
      const c = store.state.main.commission[cID + this.userID];
      if (!c) return sum;
      loaded = true;
      const advance = payAppID ? null : this.getAdvance(cID);
      const amount = payAppID
        ? c.getPayAppCommission(payAppID)
        : advance
        ? advance.amount
        : c.remainingCommission;
      return sum + (amount || 0);
    }, 0);
    return loaded ? roundPrice(total) : null;
  }

  // The Commission record backing a single sale on this paycheck, for
  // reading its rate/margin -- getSaleCommissionAmount already resolves the
  // same record but only exposes the dollar amount.
  getSaleCommission(saleID) {
    return store.state.main.commission[saleID + this.userID] || null;
  }

  // Commission on this paycheck nets below zero: one or more sales earned
  // less than was already advanced on them. The netting is the clawback
  // working — it recovers automatically against other commission on the same
  // check — but a check that nets negative overall cannot simply be paid, so
  // payroll has to see it rather than discover a negative cheque.
  //
  // Recovering a shortfall that this check cannot absorb is a held-pay
  // adjustment (users/{userID}/heldPayAdjustments, created from the Held Pay
  // History dialog): that ledger already carries a running balance either
  // direction with a reason, which is what a carried-forward chargeback is.
  get hasClawback() {
    return this.commissionTotal < 0;
  }

  // Sales on this paycheck that owe money back, for explaining the above.
  get clawbackCommissions() {
    return this.commission
      .filter((c) => c && c.hasClawback)
      .map((c) => ({ id: c.id, owedBack: c.owedBack }));
  }

  get balance() {
    return (
      this.data.amountToHeld - this.data.amountFromHeld - this.data.amountDraw
    );
  }

  get balanceFormatted() {
    const balance = this.balance;
    return (balance > 0 ? "+" : "") + formatCurrency(balance);
  }

  get override() {
    return this.hasOverride
      ? {
          amount:
            "amount" in this.data.override
              ? formatCurrency(this.data.override.amount)
              : null,

          amountCommission:
            "amountCommission" in this.data.override
              ? formatCurrency(this.data.override.amountCommission)
              : null,

          amountDraw:
            "amountDraw" in this.data.override
              ? formatCurrency(this.data.override.amountDraw)
              : null,

          amountFromHeld:
            "amountFromHeld" in this.data.override
              ? formatCurrency(this.data.override.amountFromHeld)
              : null,

          amountToHeld:
            "amountToHeld" in this.data.override
              ? formatCurrency(this.data.override.amountToHeld)
              : null,
        }
      : null;
  }

  get total() {
    // Use commissionTotal (fresh calculation) instead of stale amountCommission
    return roundPrice(
      this.commissionTotal +
        (this.data.amountFromHeld || 0) +
        (this.data.amountDraw || 0) -
        (this.data.amountToHeld || 0)
    );
  }

  // The figure a PAID check actually pays: the override totals when an
  // override is present, otherwise the live total -- falling back to the
  // stored amountCommission where no commission records are loaded (the
  // admin dialog starts no commission listeners, so commissionTotal reads 0
  // there even on a check that stored a negative amount).
  get payableTotal() {
    if (this.hasOverride) return this.overrideTotal;
    return roundPrice(
      (this.commissionTotal || this.data.amountCommission || 0) +
        (this.data.amountFromHeld || 0) +
        (this.data.amountDraw || 0) -
        (this.data.amountToHeld || 0)
    );
  }

  // No paycheck may be NEWLY PAID at a negative total. A clawback can drive
  // commissionTotal below zero; such a check may exist as a pending record
  // (that is how payroll discovers the debt) but recovery happens through
  // held pay, spread across future checks -- never by posting a negative
  // payment. Deliberately NOT a `rules` entry: rules validate state, and
  // this must gate the TRANSITION to paid. A check already paid on main with
  // a stored negative amountCommission (net-negative payoff, pre-guard) has
  // to stay editable for housekeeping, so the dialog applies this only when
  // payrollDate is being set for the first time.
  get canBePaid() {
    return this.payableTotal >= 0;
  }

  get overrideTotal() {
    return this.data.override
      ? roundPrice(
          (("amountCommission" in this.data.override
            ? this.data.override.amountCommission
            : this.data.amountCommission) || 0) +
            (("amountFromHeld" in this.data.override
              ? this.data.override.amountFromHeld
              : this.data.amountFromHeld) || 0) +
            (("amountDraw" in this.data.override
              ? this.data.override.amountDraw
              : this.data.amountDraw) || 0) -
            (("amountToHeld" in this.data.override
              ? this.data.override.amountToHeld
              : this.data.amountToHeld) || 0)
        )
      : null;
  }

  get status() {
    if (this.data.payrollDate || this.data.payment) {
      return "paid";
    } else if (this.data.overrideConfirmed) {
      return "overrideConfirmed";
    } else if (this.hasOverride) {
      return "override";
    } else {
      return "pending";
    }
  }

  get statusType() {
    const type = PAYCHECK_STATUS_TYPES[this.status];
    return type ? { ...type, ...COLORS[type.color] } : null;
  }

  get user() {
    return store.state.main.users[this.userID];
  }

  get previouslyHeld() {
    const balance = store.state.main.balances[this.userID];
    return balance ? -balance.data.total : 0;
  }

  // The held-pay balance as it will stand AFTER this paycheck.
  //
  // `previouslyHeld` reads the stored `balances/{userID}` doc, which
  // `functions/paychecks.js` only posts once the paycheck is actually paid
  // (both the create and update branches gate `addToBalance` on
  // `payment || payrollDate`). While a check is still pending, the amounts
  // being entered on it are therefore NOT reflected there, so a "Balance"
  // label backed by `previouslyHeld` reads as stale by exactly the current
  // check's To Held (reported: $7,717.83 shown, $9,303.64 expected, on a
  // check holding $1,585.81).
  //
  // `previouslyHeld` itself is deliberately left alone: the `amountFromHeld`
  // rule validates against it, and you can only draw from what is already
  // held. This getter is for display.
  get projectedHeld() {
    // Once posted, the stored balance already accounts for this check.
    if (this.data.payrollDate || this.data.payment) {
      return this.previouslyHeld;
    }
    const override = this.data.override || {};
    const toHeld =
      "amountToHeld" in override
        ? override.amountToHeld
        : this.data.amountToHeld;
    const fromHeld =
      "amountFromHeld" in override
        ? override.amountFromHeld
        : this.data.amountFromHeld;
    return roundPrice(this.previouslyHeld + (toHeld || 0) - (fromHeld || 0));
  }

  get projectedHeldFormatted() {
    return formatCurrency(this.projectedHeld);
  }

  get createdBy() {
    return this.data.createdBy
      ? store.state.main.users[this.data.createdBy]
      : null;
  }

  get modifiedBy() {
    return this.data.modifiedBy
      ? store.state.main.users[this.data.modifiedBy]
      : null;
  }
}
